/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Stack, Typography } from '@mui/material'
import { useEffect, useState, useMemo } from 'react'
import { HelpCircleIcon } from 'lucide-react'

// Store and types
import { useBulkImportStore } from '../../hooks/useBulkImportStore'
import { bulkImportStoreActions } from '../../store/bulk-import.store'
import { type SettingsBulkImport } from '../../store/bulk-import.store.types'

import { EvaluationCriteriaRespondBulkImport } from './EvaluationCriteriaRespondBulkImport'

// Components
import { LeniencySelect } from '@/views/NewBuilder/components/LeniencySelect'
import {
  type CardActivityWithId,
  MC_QUESTION_TYPE_OPTIONS,
  type LeniencyCard,
  type CardActivity,
} from '@/models/card-activity/card-activity.model'
import { LineChart } from '@/views/NewBuilder/components/IconsBuilder'
import { GradingMethodSelect } from '@/components/GradingMethodSelect'
import { useDefaultRubricId } from '@/hooks/useDefaultRubricId'
import { useRubric } from '@/hooks/useRubrics'
import RubricCard from '@/components/RubricCard'
import RubricDialog from '@/components/RubricBuilder'
import { ToggleButtonGradingMethodMCCard } from '@/components/ToggleButtonGradingMethodMCCard'
import { CustomTooltip } from '@/components/ui/tooltip'
import { useUserPlan } from '@/hooks/useUserPlan'
// New imports for standards based grading
import { useProficiencyStandards, type ProficiencyStandard } from '@/hooks/useProficiencyStandards'
import StandardSelectionDropdown from '@/components/StandardSelectionDropdown'
import TargetProficiencyLevelSelectionDropdown from '@/components/TargetProficiencyLevelSelectionDropdown'

export function GradingMethodRepeatBulkImport({
  leniency,
  onChange,
}: {
  leniency: LeniencyCard
  onChange: (value: LeniencyCard) => void
}) {
  return (
    <Stack
      sx={{
        gap: '6px',
      }}
    >
      <Typography fontWeight="medium">Leniency</Typography>
      <LeniencySelect
        difficulty={leniency}
        icon={
          <Stack
            sx={{
              bgcolor: '#86CB3C',
              padding: '6px',
              borderRadius: '12px',
              border: '1.5px solid #F4F3FF',
            }}
          >
            <LineChart />
          </Stack>
        }
        onChange={onChange}
      />
    </Stack>
  )
}

export function GradingMethodMCBulkImport({
  type,
  onChange,
}: {
  type: CardActivityWithId['MCQType']
  onChange: (type: CardActivityWithId['MCQType']) => void
}) {
  return (
    <Stack
      sx={{
        gap: '6px',
      }}
    >
      <Typography fontWeight="medium">Grading Method</Typography>
      <ToggleButtonGradingMethodMCCard value={type} onChange={onChange} />

      <Stack
        sx={{
          gap: '4px',
          alignItems: 'center',
          flexDirection: 'row',
          color: '#667085',
          width: 'fit-content',
        }}
      >
        <Typography fontWeight="medium" variant="xs">
          About AI-generated options
        </Typography>
        <CustomTooltip
          placement="right"
          title={
            <Stack sx={{ gap: '2px' }}>
              <Typography fontWeight="semibold" variant="xs">
                We&apos;ll generate answer options for you!
              </Typography>
              <Typography color="#667085" fontWeight="medium" variant="xs">
                Please review — AI might not always get it exactly right.
              </Typography>
            </Stack>
          }
        >
          <HelpCircleIcon color="#667085" size={14} />
        </CustomTooltip>
      </Stack>
    </Stack>
  )
}

// Explicit Props Interface
interface GradingMethodRespondBulkImportProps {
  gradingMethod: SettingsBulkImport['grading']['method']
  onChangeGradingMethod: (value: SettingsBulkImport['grading']['method'] | undefined) => void
  rubricId?: string
  onClickRubric: (rubricId: string, totalPoints: number) => void
  generateWithAI: boolean
  studentLevel: string
  performanceExpectations: string
  onSwitchGenerateWithAI: (generateWithAI: boolean) => void
  onChangeStudentLevel: (studentLevel: string) => void
  onChangeAIFocus: (aiFocus: string) => void
  onChangePerformanceExpectations: (performanceExpectations: string) => void
  points: number
  aiFocus: string
}

export function GradingMethodRespondBulkImport({
  gradingMethod,
  rubricId,
  generateWithAI,
  studentLevel,
  performanceExpectations,
  onChangeGradingMethod,
  onClickRubric,
  onSwitchGenerateWithAI,
  onChangeStudentLevel,
  onChangeAIFocus,
  onChangePerformanceExpectations,
  points,
  aiFocus,
}: GradingMethodRespondBulkImportProps) {
  const { isFreePlan } = useUserPlan()
  const [isRubricDialogOpen, setIsRubricDialogOpen] = useState(false)

  // Get relevant settings from store
  const settings = useBulkImportStore(store => store.settings)
  const grading = settings.grading
  const language = settings.language

  const isRubric = grading.method === 'rubric'
  const isSimple = grading.method === 'simple'
  const isStandardsBased = grading.method === 'standards_based'

  // --- Proficiency Standards Hook ---
  const {
    data: proficiencyStandards,
    isPending: isLoadingStandards,
    error: standardsError,
  } = useProficiencyStandards({
    language,
    enabled: isStandardsBased,
  })

  const selectedStandard = useMemo(() => {
    if (!isStandardsBased || !proficiencyStandards || !grading.standardId) return undefined

    return proficiencyStandards.find(s => s.id === grading.standardId)
  }, [isStandardsBased, proficiencyStandards, grading.standardId])
  // ---------------------------------

  const defaultRubricId = useDefaultRubricId()
  const rubricToUse = grading.rubricId || defaultRubricId
  const { rubric } = useRubric({
    id: rubricToUse,
    enabled: true,
  })

  useEffect(() => {
    // if (isFreePlan && grading.method !== 'manual') {
    //   // This seems to be forcing manual for free plan, might need adjustment
    //   // if standards_based should be allowed on free plan with specific permissions.
    //   onChangeGradingMethod('manual')
    //   return
    // }
    if (grading.method === 'rubric' && rubric.total_points !== points) {
      onClickRubric(rubric.id as string, rubric.total_points as number)
    }
    // Intentionally not depending on onChangeGradingMethod, onClickRubric, points which might cause loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grading.method, rubric?.total_points, rubric?.id, isFreePlan])

  const handleStandardChange = (standardId: string) => {
    const firstLevel = proficiencyStandards?.find(s => s.id === standardId)?.levels[0]

    bulkImportStoreActions.updateGrading({
      standardId: standardId,
      target_proficiency_level: firstLevel?.id,
    })
  }

  const handleTargetLevelChange = (levelId: string) => {
    bulkImportStoreActions.updateGrading({ target_proficiency_level: levelId })
  }

  return (
    <Stack
      sx={{
        gap: '12px', // Increased gap
      }}
    >
      <Typography fontWeight="medium">Grading Method</Typography>
      <GradingMethodSelect gradingMethod={grading.method} onChange={onChangeGradingMethod} />
      {isRubric ? (
        <RubricCard
          description={rubric?.description}
          name={rubric?.name}
          total_points={rubric?.total_points}
          onClick={() => {
            setIsRubricDialogOpen(true)
          }}
        />
      ) : null}
      {isSimple ? (
        <EvaluationCriteriaRespondBulkImport
          aiFocus={aiFocus}
          generateWithAI={generateWithAI}
          performanceExpectations={performanceExpectations}
          studentLevel={studentLevel}
          type="dropdown"
          onChangeAIFocus={onChangeAIFocus}
          onChangePerformanceExpectations={onChangePerformanceExpectations}
          onChangeStudentLevel={onChangeStudentLevel}
          onSwitchGenerateWithAI={onSwitchGenerateWithAI}
        />
      ) : null}
      {/* New Section for Standards Based */}
      {isStandardsBased ? (
        <Stack gap="12px" mt={1}>
          {' '}
          {/* Add margin top */}
          {isLoadingStandards ? <Typography variant="body2">Loading standards...</Typography> : null}
          {standardsError ? (
            <Typography color="error" variant="body2">
              Error loading standards.
            </Typography>
          ) : null}
          {!isLoadingStandards && !standardsError && (
            <>
              <Typography fontWeight="medium">Proficiency Standard</Typography>
              <StandardSelectionDropdown
                disabled={isLoadingStandards || !proficiencyStandards || proficiencyStandards.length === 0}
                helperText={!grading.standardId ? 'Select a proficiency standard to continue' : undefined}
                selectedStandardId={grading.standardId}
                standards={proficiencyStandards || []}
                onChange={handleStandardChange}
              />
            </>
          )}
          {/* Only show level dropdown if a standard is selected */}
          {selectedStandard && grading.standardId ? (
            <>
              <Typography fontWeight="medium">Select a Level</Typography>
              <TargetProficiencyLevelSelectionDropdown
                disabled={!selectedStandard.levels || selectedStandard.levels.length === 0}
                helperText={!grading.target_proficiency_level ? 'Select a proficiency level to continue' : undefined}
                levels={selectedStandard.levels || []}
                selectedLevelId={grading.target_proficiency_level}
                standardName={selectedStandard.name}
                onChange={handleTargetLevelChange}
              />
            </>
          ) : null}
        </Stack>
      ) : null}
      <RubricDialog
        isOnlyOneSave
        open={isRubricDialogOpen}
        rubricId={rubricToUse}
        onClose={() => {
          setIsRubricDialogOpen(false)
        }}
        // @ts-ignore
        onSave={(newRubricId, selectedRubric) => {
          onClickRubric(newRubricId as string, selectedRubric.total_points as number)
          setIsRubricDialogOpen(false)
        }}
      />
    </Stack>
  )
}
